import { orderBy, round, sumBy } from 'lodash';

type InputType = {
  allocations: Array<{
    commitmentId: string;
    allocationAmountTcy: number;
    allocationAmountLcy?: number | null;
  }>;
  includedCommitments: Array<{
    isBalancingEntity: boolean;
    _id: string;
  }>;
  valueToBalance: 'allocationAmountTcy' | 'allocationAmountLcy';
  targetAmount: number;
};

const orderAllocationsByPriority = ({
  allocations,
  includedCommitments,
  valueToBalance,
}: {
  allocations: InputType['allocations'];
  includedCommitments: InputType['includedCommitments'];
  valueToBalance: InputType['valueToBalance'];
}) => {
  const balancingEntityIds = includedCommitments
    .filter((commitment) => commitment.isBalancingEntity)
    .map((commitment) => commitment._id);

  const balancingAllocations = orderBy(
    allocations
      .map((alloc, idx) => ({
        idx,
        value: alloc[valueToBalance] ?? 0,
        commitmentId: alloc.commitmentId,
        isBalancing: balancingEntityIds.includes(alloc.commitmentId),
      }))
      .filter((item) => item.isBalancing),
    'value',
    ['desc'],
  );

  const nonBalancingAllocations = orderBy(
    allocations
      .map((alloc, idx) => ({
        idx,
        value: alloc[valueToBalance] ?? 0,
        commitmentId: alloc.commitmentId,
        isBalancing: balancingEntityIds.includes(alloc.commitmentId),
      }))
      .filter((item) => !item.isBalancing),
    'value',
    ['desc'],
  );

  return [...balancingAllocations, ...nonBalancingAllocations];
};

export default function balanceAllocations({
  allocations,
  includedCommitments,
  targetAmount,
  valueToBalance,
}: InputType) {
  const sumOfAllocations = round(sumBy(allocations, valueToBalance), 2);

  if (sumOfAllocations > 0 && sumOfAllocations !== targetAmount) {
    const variance = round(targetAmount - sumOfAllocations, 2);

    const maxAllowedVariance = allocations.length * 0.01;

    if (Math.abs(variance) > maxAllowedVariance) {
      throw new Error(
        `Sum of allocations variance ${Math.abs(
          variance,
        )} exceeds threshold ${maxAllowedVariance} for ${
          allocations.length
        } allocations`,
      );
    }

    const allocationIndicesByPriority = orderAllocationsByPriority({
      allocations,
      includedCommitments,
      valueToBalance,
    });

    let centsToDistribute = Math.round(variance * 100);
    let currentIdx = 0;

    while (
      centsToDistribute !== 0 &&
      currentIdx < allocationIndicesByPriority.length
    ) {
      const idx = allocationIndicesByPriority[currentIdx].idx;
      const adjustment = centsToDistribute > 0 ? 0.01 : -0.01;

      allocations[idx][valueToBalance] = round(
        (allocations[idx][valueToBalance] ?? 0) + adjustment,
        2,
      );

      centsToDistribute += adjustment > 0 ? -1 : 1;
      currentIdx++;
    }
  }
}
